import { TranscriptData } from "../../../shared/interfaces/Transcript";
import { countWords, paragraphText } from "./timeFormat";

export interface EntryValidation {
  /** Word-gate tripped (large text addition) and the range hasn't been re-confirmed. */
  needsRangeReselect: boolean;
  /** start >= end, negative, or beyond the video duration. */
  invalidRange: boolean;
  /** The entry has no text — empty entries are not allowed. */
  emptyText: boolean;
}

export interface TranscriptValidationResult {
  /** Keyed by paragraph `_uid`. Entries with no issues may be absent. */
  entries: Record<string, EntryValidation>;
  /** True when every entry is clean (does not consider dirtiness). */
  allValid: boolean;
}

const EMPTY: EntryValidation = {
  needsRangeReselect: false,
  invalidRange: false,
  emptyText: false,
};

interface ValidateArgs {
  draft: TranscriptData;
  original: TranscriptData | null;
  /** Paragraph _uids whose range the user has (re)confirmed this session. */
  rangeConfirmed: Set<string>;
  maxWordChange: number;
  /** Video duration in seconds, when known, to bound entry end times. */
  duration?: number;
}

/**
 * Pure validation over the draft transcript. Computes, per entry:
 *  - word-gate: net words added vs. the loaded text exceeds the threshold and
 *    the range has not been re-confirmed;
 *  - invalidRange: start >= end, negative, or past the video duration;
 *  - emptyText: the entry has no text.
 * Entries are allowed to overlap in time — overlap is not validated.
 */
export function validateTranscriptDraft({
  draft,
  original,
  rangeConfirmed,
  maxWordChange,
  duration,
}: ValidateArgs): TranscriptValidationResult {
  const paragraphs = draft.paragraphs ?? [];

  // Original word counts keyed by _uid (entries added this session have none).
  const originalWords = new Map<string, number>();
  (original?.paragraphs ?? []).forEach((p) => {
    if (p._uid) originalWords.set(p._uid, countWords(paragraphText(p)));
  });

  const result: Record<string, EntryValidation> = {};

  // Word-gate + invalidRange + emptyText (per entry). Overlaps are permitted.
  for (const p of paragraphs) {
    const uid = p._uid;
    if (!uid) continue;
    const baseline = originalWords.get(uid) ?? 0;
    const added = Math.max(0, countWords(paragraphText(p)) - baseline);
    const needsRangeReselect =
      added > maxWordChange && !rangeConfirmed.has(uid);

    const invalidRange =
      !(p.start < p.end) ||
      p.start < 0 ||
      (typeof duration === "number" && duration > 0 && p.end > duration + 0.5);

    const emptyText = paragraphText(p).trim() === "";

    if (needsRangeReselect || invalidRange || emptyText) {
      result[uid] = { needsRangeReselect, invalidRange, emptyText };
    }
  }

  const allValid = Object.values(result).every(
    (v) => !v.needsRangeReselect && !v.invalidRange && !v.emptyText,
  );

  return { entries: result, allValid };
}

/** Convenience: validation for a single entry, falling back to a clean record. */
export const entryValidation = (
  result: TranscriptValidationResult,
  uid: string | undefined,
): EntryValidation => (uid && result.entries[uid]) || EMPTY;
